import axios from 'axios'
import CONFIG from 'config/constants'
import ls from 'utils/ls'

type RefreshResponse = {
  accessToken: string
  accessTokenExpiration: string
}

let refreshPromise: Promise<string | null> | null = null
let onAuthFailure: (() => void) | null = null
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | undefined

export function setOnAuthFailure(callback: () => void) {
  onAuthFailure = callback
}

export function isSessionActive(): boolean {
  const lastActivityTime = localStorage.getItem('lastActivityTime')
  if (!lastActivityTime) return true

  const elapsed = Date.now() - parseInt(lastActivityTime, 10)
  return elapsed < CONFIG.INACTIVITY_TIMEOUT_MS
}

export function getAccessToken(): string | null {
  return ls.get(CONFIG.LSKEY_TOKEN)
}

export function getRefreshToken(): string | null {
  return ls.get(CONFIG.LSKEY_REFRESH_TOKEN)
}

export function clearAuthTokens(): void {
  ls.remove(CONFIG.LSKEY_TOKEN)
  ls.remove(CONFIG.LSKEY_REFRESH_TOKEN)
  ls.remove(CONFIG.LSKEY_ACCESS_TOKEN_EXPIRY)
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer)
    proactiveRefreshTimer = undefined
  }
}

export function storeAuthTokens(
  accessToken: string,
  refreshToken: string,
  accessTokenExpiration?: string | Date
): void {
  ls.add(CONFIG.LSKEY_TOKEN, accessToken)
  ls.add(CONFIG.LSKEY_REFRESH_TOKEN, refreshToken)

  if (accessTokenExpiration) {
    ls.add(
      CONFIG.LSKEY_ACCESS_TOKEN_EXPIRY,
      new Date(accessTokenExpiration).toISOString()
    )
  }

  scheduleProactiveRefresh()
}

export function shouldRefreshAccessToken(): boolean {
  const expiry = ls.get(CONFIG.LSKEY_ACCESS_TOKEN_EXPIRY)
  if (!expiry) return !!getRefreshToken()

  return Date.now() >= new Date(expiry).getTime() - CONFIG.ACCESS_TOKEN_REFRESH_SKEW_MS
}

function migrateLegacyToken(): void {
  const accessToken = getAccessToken()
  const refreshToken = getRefreshToken()

  // Previously the app stored the API refresh token under LSKEY_TOKEN.
  if (!refreshToken && accessToken) {
    ls.add(CONFIG.LSKEY_REFRESH_TOKEN, accessToken)
  }
}

export function scheduleProactiveRefresh(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer)
    proactiveRefreshTimer = undefined
  }

  const expiry = ls.get(CONFIG.LSKEY_ACCESS_TOKEN_EXPIRY)
  if (!expiry || !getRefreshToken()) return

  const refreshAt =
    new Date(expiry).getTime() - CONFIG.ACCESS_TOKEN_REFRESH_SKEW_MS
  const delay = Math.max(refreshAt - Date.now(), 0)

  proactiveRefreshTimer = setTimeout(() => {
    if (!isSessionActive()) return
    refreshAccessToken().then((token) => {
      if (token) scheduleProactiveRefresh()
    })
  }, delay)
}

export async function ensureAccessToken(): Promise<string | null> {
  migrateLegacyToken()

  const accessToken = getAccessToken()
  const refreshToken = getRefreshToken()

  if (!accessToken && !refreshToken) return null

  if (!ls.get(CONFIG.LSKEY_ACCESS_TOKEN_EXPIRY) || shouldRefreshAccessToken()) {
    return refreshAccessToken()
  }

  scheduleProactiveRefresh()
  return accessToken
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      if (!isSessionActive()) {
        onAuthFailure?.()
        return null
      }

      migrateLegacyToken()
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        onAuthFailure?.()
        return null
      }

      const { data } = await axios.get<RefreshResponse>(
        `${process.env.REACT_APP_API_HOST}/refresh`,
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            'X-API-Key': CONFIG.API_KEY,
            'Content-Type': 'application/json',
          },
        }
      )

      ls.add(CONFIG.LSKEY_TOKEN, data.accessToken)
      if (data.accessTokenExpiration) {
        ls.add(
          CONFIG.LSKEY_ACCESS_TOKEN_EXPIRY,
          new Date(data.accessTokenExpiration).toISOString()
        )
      }

      scheduleProactiveRefresh()
      return data.accessToken
    } catch {
      onAuthFailure?.()
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}
